import React from 'react';
import { PLAN_SKELETON_IMAGES } from '../../data/featuredCityPreviews';
import { SkeletonText, SkeletonValue } from './SkeletonValue';

type PlaneCardSkeletonProps = {
  imageSrc: string;
};

const PlaneCardSkeleton: React.FC<PlaneCardSkeletonProps> = ({ imageSrc }) => {
  return (
    <article
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-3xl border border-[rgba(74,33,71,0.1)] bg-white"
      aria-hidden
    >
      <div className="relative aspect-[16/10] w-full shrink-0 bg-gray-100">
        <img
          src={imageSrc}
          alt=""
          loading="eager"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-between p-5 sm:p-6">
        <header className="space-y-1">
          <SkeletonText className="h-6 w-[88%] sm:h-7" />
          <p className="flex flex-wrap items-center gap-x-1 pt-0.5 text-sm leading-snug text-gray-500">
            <span>Creado por</span>
            <SkeletonText className="h-4 w-20" />
            <span>(</span>
            <SkeletonValue className="h-4 w-6" />
            <span>)</span>
          </p>
        </header>

        <div className="mt-4 overflow-hidden rounded-2xl border border-[rgba(74,33,71,0.1)] bg-chisme-bg sm:mt-5">
          <div className="grid grid-cols-2 gap-0 border-b border-[rgba(74,33,71,0.1)]">
            <div className="border-r border-[rgba(74,33,71,0.1)] px-3 py-3 text-left sm:px-4 sm:py-3.5">
              <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                Fecha
              </p>
              <div className="mt-1">
                <SkeletonText className="h-4 w-24 sm:h-[18px] sm:w-28" />
              </div>
            </div>
            <div className="px-3 py-3 text-left sm:px-4 sm:py-3.5">
              <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                Apuntadas
              </p>
              <div className="mt-1">
                <SkeletonValue className="mx-0 h-4 w-8 sm:h-5 sm:w-10" />
              </div>
            </div>
          </div>
          <div className="px-3 py-3 sm:px-4 sm:py-3.5">
            <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
              Barrio
            </p>
            <div className="mt-1">
              <SkeletonText className="h-4 w-[70%] sm:h-5" />
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-[rgba(74,33,71,0.1)] pt-4 sm:mt-5">
          <span className="font-jakarta text-sm font-semibold text-chisme-primary">Ver detalles</span>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[rgba(74,33,71,0.1)] text-chisme-primary">
            <svg className="-mr-px h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </article>
  );
};

type PlaneCardSkeletonGridProps = {
  count?: number;
};

export function PlaneCardSkeletonGrid({ count = 6 }: PlaneCardSkeletonGridProps) {
  return (
    <div
      className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Cargando planes"
    >
      {Array.from({ length: count }, (_, i) => (
        <PlaneCardSkeleton
          key={i}
          imageSrc={PLAN_SKELETON_IMAGES[i % PLAN_SKELETON_IMAGES.length]}
        />
      ))}
      <span className="sr-only">Cargando planes…</span>
    </div>
  );
}

export default PlaneCardSkeleton;
